import React from 'react';
import PropTypes from 'prop-types';
import { BATCH_COLUMN, BUTTON, TEXT, STATUS, EDIT_BOX_STATE } from '../../../../constants';
import { MfgTable } from './MfgTable';
import { Button, Image } from '../../../shared';
import { calculateTotalSewDamagedQty, getAvailableSewCapacity } from './utils/batchHelpers';

/**
 * BatchDetails - Displays batch table and creation buttons
 * Manages two types of report creation: Cutting and Sewing
 *
 * Business Rules:
 * - Cutting reports can be created anytime (creates new batch from scratch)
 * - Sewing reports require: available cut units AND no draft batches
 * - Sewing reports work independently of existing batches (new sewing-only batch)
 */
export const BatchDetails = ({
  setField,
  lot,
  setSelectedBatch,
  editBoxState,
  setEditBoxState,
  image,
  isLoading,
  setReportType,
  availableSewCapacity = 0
}) => {
  /**
   * Initialize cutting report creation
   * Sets report type and opens creation form
   */
  const handleCreateCuttingReport = () => {
    setSelectedBatch(null);
    setReportType('cutting');
    setEditBoxState(EDIT_BOX_STATE.CREATE);
  };

  /**
   * Initialize sewing report creation
   * Creates independent sewing-only batch using upsert endpoint
   */
  const handleCreateSewingReport = () => {
    setSelectedBatch(null);
    setReportType('sewing');
    setEditBoxState(EDIT_BOX_STATE.CREATE);
  };

  /**
   * Check if any batch is in draft state (not packed)
   * Business Rule: Cannot create sewing report if draft batches exist
   * This prevents data conflicts and ensures sequential processing
   */
  const hasDraftBatches = lot?.batch?.some(batch => batch.state !== 'packed') || false;

  /**
   * Determine if "Create Sew Report" button should be enabled
   * Requirements:
   * 1. Must have available cut units (capacity > 0) - passed from parent
   * 2. No draft batches (prevents workflow conflicts)
   */
  const canCreateSewReport = availableSewCapacity > 0 && !hasDraftBatches;

  /**
   * Determine if "Create Batch" button should be hidden
   * Hide when:
   * 1. Lot is ready (completed) AND no remaining quantity
   * 2. OR lot status is explicitly 'Not Ready' (raw material unavailable)
   */
  const hideCreateButton = (lot) => {
    return (
      (lot?.ready_status !== 'Not Ready' && lot?.remaining_qty <= 0) || (lot?.ready_status === 'Not Ready')
    );
  };
  
  /**
   * Determine if creation buttons should be hidden
   * Hide when ready_unit >= order_unit (lot is complete)
   */
  const shouldHideCreationButtons = (lot?.ready_status === 'Not Ready') || lot?.ready_qty >= lot?.total_qty || lot.remaining_qty <= 0;
  /**
   * Handle batch row click - determines which edit mode to open
   * Logic follows manufacturing workflow sequence:
   * 1. If packed → Open in "Ready" mode (view/generate BLP)
   * 2. If sewn + dyeing → Open in "Ready for Dye" mode
   * 3. If sewn (no dyeing) → Open in "Pack" mode (add packing data)
   * 4. If cut → Open in "Sew" mode (add sewing data)
   * 5. Otherwise → Open in "Cut" mode (add cutting data)
   */
  const editHandler = ({ row, e }) => {
    e.stopPropagation();
    setSelectedBatch(row);
    if (row.pack_qty && row.pack_date) {
      setField(STATUS.READY);
      setEditBoxState(EDIT_BOX_STATE.EDIT);
    } else if (row.sew_qty && lot.dyeing) {
      setField(STATUS.READY);
      setEditBoxState(EDIT_BOX_STATE.EDIT);
    } else if (row.sew_qty) {
      setField(STATUS.PACK);
      setEditBoxState(EDIT_BOX_STATE.EDIT);
    } else if (row.cut_qty) {
      setField(STATUS.SEW);
      setEditBoxState(EDIT_BOX_STATE.EDIT);
    } else {
      setField(STATUS.CUT);
      setEditBoxState(EDIT_BOX_STATE.EDIT);
    }
  };

  return (
    <div className="w-100 row info-detail-container">
      <div className="col-2">
        <Image src={`data:image/png;base64,${image}`} alt={''} className="img-fluid w-100" />
      </div>
      {isLoading ? (<div className="text-center my-2">
        <span className="spinner-border spinner-border-sm text-primary" role="status" />
        <span className="ms-2">Loading...</span>
      </div>) : <div className="col-10">
        <div className="table-container-2 table-2 position-relative mb-3 w-100">
          {lot?.batch?.length > 0 ? (
            <MfgTable
              lot={lot}
              column={BATCH_COLUMN}
              data={lot.batch}
              dyeing={lot.dyeing}
              clickHandler={editHandler}
              ready_status={lot?.ready_status}
            />
          ) : (
            <div className="lot-info">
              <p className="status-message">
                {hideCreateButton(lot)
                  ? 'Raw material not available for this lot'
                  : editBoxState === 0
                    ? TEXT.NO_BATCH_AVAILABLE
                    : ''}
              </p>

              {Array.isArray(lot?.not_ready_lots) &&
                lot.not_ready_lots.length > 0 && (
                  <ol className="not-ready-lots">
                    {lot.not_ready_lots.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ol>
                )}
            </div>
          )}
        </div>
        <div className="col-offset-2 col-10 d-flex flex-column gap-3 w-100">
          {/**
           * Show creation buttons when:
           * 1. Not currently creating or editing a batch (form not open)
           * 2. Ready units < Order units (lot not complete)
           */}
          {editBoxState !== EDIT_BOX_STATE.CREATE &&
            editBoxState !== EDIT_BOX_STATE.EDIT &&
            !shouldHideCreationButtons && (
              <div className="d-flex align-items-center gap-3">
                {/* Create Cutting Report - Always available when buttons shown */}
                <Button
                  clickHandler={handleCreateCuttingReport}
                  className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
                  {BUTTON.CREATE_BATCH}
                </Button>
                {/* Create Sewing Report - Conditionally enabled based on capacity */}
                <Button
                  clickHandler={handleCreateSewingReport}
                  disabled={!canCreateSewReport}
                  className={`border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3 ${
                    !canCreateSewReport ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  style={!canCreateSewReport ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                  title={!canCreateSewReport ? 'No cut units available for sewing or an existing cut report is not complete' : 'Create Sewing Report'}>
                  {BUTTON.CREATE_SEW_REPORT}
                </Button>
              </div>
            )}
        </div>
      </div>}
    </div>
  );
};

BatchDetails.propTypes = {
  setField: PropTypes.func.isRequired,
  lot: PropTypes.shape({
    batch: PropTypes.arrayOf(
      PropTypes.shape({
        state: PropTypes.string,
        pack_qty: PropTypes.number,
        pack_date: PropTypes.string,
        sew_qty: PropTypes.number,
        cut_qty: PropTypes.number
      })
    ),
    dyeing: PropTypes.bool,
    ready_status: PropTypes.string,
    remaining_qty: PropTypes.number,
    not_ready_lots: PropTypes.arrayOf(PropTypes.string)
  }),
  setSelectedBatch: PropTypes.func.isRequired,
  editBoxState: PropTypes.number.isRequired,
  setEditBoxState: PropTypes.func.isRequired,
  image: PropTypes.string,
  isLoading: PropTypes.bool,
  setReportType: PropTypes.func.isRequired,
  availableSewCapacity: PropTypes.number
};

BatchDetails.defaultProps = {
  lot: null,
  image: '',
  isLoading: false,
  availableSewCapacity: 0
};
